/**
 * What is this file?
 *
 * Decided from the bytes, and the extension is only ever a tie-break. Every
 * one of these has been seen in the wild: a `.csv` that is tab-separated
 * because somebody renamed it, a `.txt` that is a paste out of Excel, a `.xlsx`
 * that is really a CSV Excel was willing to open, and a JSON export with no
 * extension at all. Trusting the name means telling a user their file is broken
 * when it is perfectly readable.
 *
 * It is also the security-relevant half of the decision: an `.xlsx` is a zip,
 * and a zip is the one shape here that can be hostile. Recognising it by its
 * magic bytes rather than its name is what makes the zip caps in `xlsx.ts`
 * apply to everything that is actually a zip.
 */
import type { SourceFormat } from "./types.js";

/** `PK\x03\x04` — the local file header every zip starts with. */
const ZIP = [0x50, 0x4b, 0x03, 0x04];
/** The two other zip signatures: an empty archive, and a spanned one. */
const ZIP_EMPTY = [0x50, 0x4b, 0x05, 0x06];
const ZIP_SPANNED = [0x50, 0x4b, 0x07, 0x08];

export function sniffBytes(bytes: Uint8Array, filename?: string): SourceFormat {
  if (starts(bytes, ZIP) || starts(bytes, ZIP_EMPTY) || starts(bytes, ZIP_SPANNED)) return "xlsx";
  // The old binary Excel format, which this product does not read. Named here
  // so the error can say "save it again as .xlsx" rather than "unknown file".
  if (starts(bytes, [0xd0, 0xcf, 0x11, 0xe0])) return "unknown";
  // Anything else is text, and the text sniffer is better informed than the
  // extension is. Only the first few kilobytes are decoded: a delimiter and a
  // leading brace are both visible immediately, and decoding forty megabytes to
  // answer a yes/no question is wasteful.
  const head = new TextDecoder("utf-8", { fatal: false }).decode(bytes.subarray(0, 64 * 1024));
  return sniffText(head, filename);
}

export function sniffText(text: string, filename?: string): SourceFormat {
  const body = (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text).trimStart();
  if (!body) return "unknown";

  // JSON announces itself. NDJSON does too, on every line, which is what tells
  // it apart from a JSON document that happens to be pretty-printed.
  if (body[0] === "{" || body[0] === "[") {
    const lines = body.split(/\r?\n/).filter((l) => l.trim());
    const everyLineIsADocument = lines.length > 1 && lines.slice(0, 10).every((l) => /^\s*[{[]/.test(l) && /[}\]]\s*,?$/.test(l.trim()));
    if (everyLineIsADocument) return "ndjson";
    return "json";
  }

  const sample = body.split(/\r?\n/).slice(0, 20).join("\n");
  const tabs = (sample.match(/\t/g) ?? []).length;
  const commas = (sample.match(/,/g) ?? []).length;
  const semis = (sample.match(/;/g) ?? []).length;
  if (tabs > 0 && tabs >= commas && tabs >= semis) return "tsv";
  if (commas > 0 || semis > 0) return "csv";

  // One column, no delimiter anywhere. Still a perfectly good list of values, so
  // the extension gets its only say here.
  const ext = filename?.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  if (ext === "tsv" || ext === "tab") return "tsv";
  if (ext === "json") return "json";
  if (ext === "ndjson" || ext === "jsonl") return "ndjson";
  return "csv";
}

function starts(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((b, i) => bytes[i] === b);
}

/**
 * A dataset name from a file name.
 *
 * `Client portfolios Aug 2026.xlsx` → `Client portfolios Aug 2026`. The
 * extension goes and nothing else does: the user named the file, and a name
 * they chose is better than anything derivable from the contents.
 */
export function labelFromFilename(filename: string | undefined, fallback: string): string {
  if (!filename) return fallback;
  const base = filename.replace(/\\/g, "/").split("/").pop() ?? filename;
  const stem = base.replace(/\.[A-Za-z0-9]{1,8}$/, "").trim();
  return stem || fallback;
}
